import { useEffect, useState } from 'preact/hooks';
import { PV_CSS, renderPvDocument } from '../../shared/vouchers/document';
import type { InboxBody, InboxItem } from '../../shared/vouchers/finance';
import type { PaymentsPageBody, VoucherBody } from '../../shared/vouchers/voucher';
import { DateHint } from '../components/DateHint';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiBytes, apiGet, apiSend, apiUpload, saveBytes } from '../lib/api';
import { formatDate } from '../lib/dates';

// Payment vouchers (screen 23, P4.4). The PO's payments with their vouchers (raise one per
// payment, paying one recorded invoice); the voucher with its six-item checklist, where "Send to
// Finance" stays off until every item is ticked; and the A4 voucher for printing (D60).

type Load<T> =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: T };

const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );

function useLoad<T>(path: `/api/${string}`, reload: number): Load<T> {
  const [load, setLoad] = useState<Load<T>>({ kind: 'loading' });
  useEffect(() => {
    let live = true;
    apiGet<T>(path)
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [path, reload]);
  return load;
}

function Status<T>({ load }: { load: Exclude<Load<T>, { kind: 'ok' }> }) {
  const { t } = useI18n();
  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  return (
    <section class="card card--error" role="alert">
      <p>
        {load.code === 'forbidden'
          ? t('review.error.forbidden')
          : load.code === 'not_found'
            ? t('pv.error.notFound')
            : load.code === 'invalid_transition'
              ? t('dl.error.noPo')
              : t('req.loadError')}
      </p>
      {load.reference && (
        <p class="caption" dir="ltr">
          {t('error.reference', { ref: load.reference })}
        </p>
      )}
    </section>
  );
}

const PILL: Record<string, string> = {
  Draft: 'pill--neutral',
  WithFinance: 'pill--info',
  Returned: 'pill--warning',
  Received: 'pill--info',
  Paid: 'pill--success',
};

export function PaymentsPage({ prNo }: { prNo: string }) {
  const { t } = useI18n();
  const load = useLoad<PaymentsPageBody>(`/api/prs/${encodeURIComponent(prNo)}/payments`, 0);
  const [choice, setChoice] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; ref: string | null } | null>(null);
  if (load.kind !== 'ok') return <Status load={load} />;
  const data = load.data;
  const cur = data.currency;

  async function raise(row: number) {
    const invoiceId = choice[row] ?? data.invoices[0]?.invoiceId ?? '';
    if (!invoiceId) return;
    setBusy(true);
    setNote(null);
    try {
      const out = await apiSend<{ pvNo: string }>(
        'POST',
        `/api/prs/${encodeURIComponent(prNo)}/payments`,
        { etag: data.etag, row, invoiceId },
      );
      window.location.hash = `#/vouchers/${out.pvNo}`;
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setNote({
        text: e?.code === 'conflict' ? t('inv.err.conflict') : t('dl.err.save'),
        ref: e?.correlationId ?? null,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="stack">
      <p class="caption">
        <a href="#/queue">{t('nav.queue')}</a> /{' '}
        <a href={`#/requests/${prNo}`} dir="ltr">
          {prNo}
        </a>{' '}
        / <span dir="ltr">{data.poNo}</span>
      </p>
      <h2 class="detail-title" dir="auto">
        {data.title}
      </h2>
      <p class="caption">
        <span dir="auto">{data.supplierName}</span> · {t('pv.poTotal')}{' '}
        <span dir="ltr">
          {cur} {money(data.poTotal)}
        </span>
      </p>
      {note && (
        <div class="form-problem" role="alert">
          <p>{note.text}</p>
          {note.ref && (
            <p class="caption" dir="ltr">
              {t('error.reference', { ref: note.ref })}
            </p>
          )}
        </div>
      )}
      <section class="card" aria-labelledby="pay-rows">
        <h3 id="pay-rows" class="card__title">
          {t('pv.payments')}
        </h3>
        <ul class="action-list">
          {data.rows.map((r) => (
            <li key={r.row} class="pv-row">
              <span>
                <span class="req-row__title">
                  {t(`pv.milestone.${r.milestone}` as MessageKey)}
                  {r.label && ` · ${r.label}`}
                </span>
                <span class="caption cell-sub">
                  {r.pct}% ·{' '}
                  <span dir="ltr">
                    {cur} {money(r.amount)}
                  </span>
                </span>
              </span>
              {r.voucher ? (
                <span class="actions">
                  <span class={`pill ${PILL[r.voucher.status] ?? 'pill--neutral'}`}>
                    {t(`pv.status.${r.voucher.status}` as MessageKey)}
                  </span>
                  <a class="button button--small" href={`#/vouchers/${r.voucher.pvNo}`}>
                    <span dir="ltr">{r.voucher.pvNo}</span>
                  </a>
                </span>
              ) : data.invoices.length === 0 ? (
                <span class="caption">{t('pv.noInvoice')}</span>
              ) : (
                <span class="actions">
                  <label class="visually-hidden" for={`pv-inv-${r.row}`}>
                    {t('pv.invoiceFor', { n: String(r.row + 1) })}
                  </label>
                  <select
                    id={`pv-inv-${r.row}`}
                    class="po-input"
                    value={choice[r.row] ?? data.invoices[0]?.invoiceId}
                    onChange={(e) =>
                      setChoice({ ...choice, [r.row]: (e.target as HTMLSelectElement).value })
                    }
                  >
                    {data.invoices.map((i) => (
                      <option key={i.invoiceId} value={i.invoiceId}>
                        {i.invoiceNo} · {cur} {money(i.total)}
                        {i.matched ? '' : ` · ${t('inv.mismatch')}`}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    class="button button--primary button--small"
                    disabled={busy || !data.canCreate}
                    onClick={() => void raise(r.row)}
                  >
                    {t('pv.raise')}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
        <p class="caption">{t('pv.oneEach')}</p>
      </section>
    </div>
  );
}

function Row({ label, children }: { label: string; children: preact.ComponentChildren }) {
  return (
    <div class="pv-field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Finance's decisions on a voucher (P4.6): receive, return with a remark, pay with a reference. */
function FinancePanel({ v, onDone }: { v: VoucherBody; onDone: (text: string) => void }) {
  const { t } = useI18n();
  const [remark, setRemark] = useState('');
  const [ref, setRef] = useState('');
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<{ text: string; ref: string | null } | null>(null);
  const can = (a: 'receive' | 'return' | 'pay') => v.financeActions.includes(a);

  async function act(body: Record<string, string>) {
    setBusy(true);
    setProblem(null);
    try {
      await apiSend('POST', `/api/vouchers/${encodeURIComponent(v.pvNo)}/finance`, {
        ...body,
        etag: v.etag,
      });
      onDone(t(`fin.done.${body.action}` as MessageKey));
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setErrors(
        Object.fromEntries(Object.keys(e?.fields ?? {}).map((k) => [k, t('fin.err.field')])),
      );
      setProblem({
        text:
          e?.code === 'preparer_cannot_approve'
            ? t('fin.err.preparer')
            : e?.code === 'conflict' || e?.code === 'invalid_transition'
              ? t('pv.err.conflict')
              : t('dl.err.save'),
        ref: e?.correlationId ?? null,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section class="card stack" aria-labelledby="fin-act">
      <h3 id="fin-act" class="card__title">
        {t('fin.title')}
      </h3>
      {problem && (
        <div class="form-problem" role="alert">
          <p>{problem.text}</p>
          {problem.ref && (
            <p class="caption" dir="ltr">
              {t('error.reference', { ref: problem.ref })}
            </p>
          )}
        </div>
      )}
      {can('receive') && (
        <button
          type="button"
          class="button button--primary"
          disabled={busy}
          onClick={() => void act({ action: 'receive' })}
        >
          {t('fin.receive')}
        </button>
      )}
      {can('pay') && (
        <div class="stack">
          <div class="field">
            <label for="fin-ref">{t('fin.ref')}</label>
            <input
              id="fin-ref"
              dir="ltr"
              maxLength={60}
              value={ref}
              aria-invalid={errors.paymentRef ? true : undefined}
              onInput={(e) => setRef((e.target as HTMLInputElement).value)}
            />
            {errors.paymentRef && <p class="field__error">{errors.paymentRef}</p>}
          </div>
          <div class="field">
            <label for="fin-date">{t('fin.paidOn')}</label>
            <input
              id="fin-date"
              type="date"
              dir="ltr"
              value={paidOn}
              aria-invalid={errors.paidOn ? true : undefined}
              aria-describedby="fin-date-when"
              onInput={(e) => setPaidOn((e.target as HTMLInputElement).value)}
            />
            <DateHint id="fin-date-when" value={paidOn} />
            {errors.paidOn && <p class="field__error">{errors.paidOn}</p>}
          </div>
          <button
            type="button"
            class="button button--primary"
            disabled={busy}
            onClick={() => {
              if (!ref.trim()) {
                setErrors({ paymentRef: t('fin.err.ref') });
                return;
              }
              void act({ action: 'pay', paymentRef: ref.trim(), paidOn });
            }}
          >
            {t('fin.pay')}
          </button>
        </div>
      )}
      {can('return') && (
        <div class="stack">
          <div class="field">
            <label for="fin-remark">{t('fin.remark')}</label>
            <textarea
              id="fin-remark"
              rows={3}
              maxLength={1000}
              dir="auto"
              value={remark}
              aria-invalid={errors.remark ? true : undefined}
              onInput={(e) => setRemark((e.target as HTMLTextAreaElement).value)}
            />
            {errors.remark && <p class="field__error">{errors.remark}</p>}
          </div>
          <button
            type="button"
            class="button button--danger"
            disabled={busy}
            onClick={() => {
              if (!remark.trim()) {
                setErrors({ remark: t('fin.err.remark') });
                return;
              }
              void act({ action: 'return', remark: remark.trim() });
            }}
          >
            {t('fin.return')}
          </button>
        </div>
      )}
    </section>
  );
}

export function VoucherPage({ pvNo }: { pvNo: string }) {
  const { t, lang } = useI18n();
  const [reload, setReload] = useState(0);
  const load = useLoad<VoucherBody>(`/api/vouchers/${encodeURIComponent(pvNo)}`, reload);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string; ref?: string | null } | null>(null);
  if (load.kind !== 'ok') return <Status load={load} />;
  const v = load.data;
  const cur = v.currency;
  const ticked = v.checklist.filter((c) => c.ok).length;

  async function exportPack() {
    setBusy(true);
    setNote({ ok: true, text: t('pack.working') });
    try {
      const list = await apiGet<{
        packName: string;
        parts: { key: string; kind: string; name: string }[];
      }>(`/api/vouchers/${encodeURIComponent(pvNo)}/pack`);
      const label = (p: { key: string; kind: string }) =>
        p.kind === 'dn' ? p.key.slice(3) : t(`pack.part.${p.kind}` as MessageKey);
      const files = [];
      const missing: string[] = [];
      const failed: string[] = [];
      let failRef: string | null = null;
      for (const p of list.parts) {
        if (!p.name) {
          missing.push(label(p));
          continue;
        }
        try {
          const f = await apiBytes(
            `/api/vouchers/${encodeURIComponent(pvNo)}/pack/${encodeURIComponent(p.key)}`,
          );
          files.push({ label: label(p), ...f });
        } catch (err) {
          // On file but could not be fetched: shown apart from "not on file", with a reference.
          failed.push(label(p));
          failRef ??= err instanceof ApiError ? err.correlationId : null;
        }
      }
      // pdf-lib loads only when a pack is made.
      const { mergePack } = await import('../../shared/vouchers/merge');
      const pack = await mergePack(files, [...missing, ...failed]);
      saveBytes(pack.bytes, list.packName, 'application/pdf');
      let filed = true;
      try {
        await apiUpload(
          `/api/vouchers/${encodeURIComponent(pvNo)}/pack`,
          new File([pack.bytes as Uint8Array<ArrayBuffer>], list.packName, {
            type: 'application/pdf',
          }),
        );
      } catch {
        filed = false;
      }
      const size = (pack.bytes.byteLength / (1024 * 1024)).toFixed(1);
      const unread = [...failed, ...pack.skipped];
      setNote({
        ok: filed && missing.length === 0 && unread.length === 0,
        text: [
          t('pack.done', { pages: String(pack.pages), size }),
          filed ? t('pack.filed') : t('pack.notFiled'),
          missing.length ? t('pack.missing', { items: missing.join(', ') }) : '',
          unread.length ? t('pack.unread', { items: unread.join(', ') }) : '',
        ]
          .filter(Boolean)
          .join(' '),
        ref: failRef,
      });
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setNote({ ok: false, text: t('pack.failed'), ref: e?.correlationId ?? null });
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    setBusy(true);
    setNote(null);
    try {
      await apiSend('POST', `/api/vouchers/${encodeURIComponent(pvNo)}/send`, { etag: v.etag });
      setNote({ ok: true, text: t('pv.sent') });
      setReload((r) => r + 1);
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setNote({
        ok: false,
        text:
          e?.code === 'checklist_incomplete'
            ? t('pv.err.incomplete')
            : e?.code === 'conflict'
              ? t('pv.err.conflict')
              : t('dl.err.save'),
        ref: e?.correlationId ?? null,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="stack">
      <p class="caption">
        <a href={`#/requests/${v.pr.prNo}/payments`}>{t('pv.payments')}</a> /{' '}
        <span dir="ltr">{v.po.poNo}</span>
      </p>
      <div class="detail-head">
        <h2 class="detail-title">
          {t('pv.title')} <span dir="ltr">{v.pvNo}</span>
        </h2>
        <span
          class={`pill ${v.status === 'Draft' ? (v.complete ? 'pill--success' : 'pill--warning') : (PILL[v.status] ?? 'pill--neutral')}`}
        >
          {v.status === 'Draft'
            ? v.complete
              ? t('pv.ready')
              : t('pv.notReady', { n: String(ticked) })
            : t(`pv.status.${v.status}` as MessageKey)}
        </span>
      </div>
      <p class="caption">
        {v.vertical} · {v.office} · {t('pv.lead')}
      </p>
      {note && (
        <div class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
          <p>{note.text}</p>
          {note.ref && (
            <p class="caption" dir="ltr">
              {t('error.reference', { ref: note.ref })}
            </p>
          )}
        </div>
      )}

      {v.status === 'Returned' && v.financeRemark && (
        <p class="notice notice--action" dir="auto">
          <strong>{t('fin.returnedBy')}:</strong> {v.financeRemark}
        </p>
      )}
      <div class="pv-layout">
        <div class="pv-cards">
          <section class="card" aria-labelledby="pv-pr">
            <h3 id="pv-pr" class="card__title">
              {t('pv.card.pr')}
            </h3>
            <dl class="pv-fields">
              <Row label={t('pv.f.pr')}>
                <a href={`#/requests/${v.pr.prNo}`} dir="ltr">
                  {v.pr.prNo}
                </a>
              </Row>
              <Row label={t('pv.f.for')}>
                <span dir="auto">{v.pr.title}</span>
              </Row>
              <Row label={t('pv.f.requester')}>
                {v.pr.requesterName}, {v.pr.department}
              </Row>
              <Row label={t('pv.f.approved')}>
                {v.pr.approvedAt ? formatDate(v.pr.approvedAt, lang) : '—'}
              </Row>
            </dl>
          </section>
          <section class="card" aria-labelledby="pv-po">
            <h3 id="pv-po" class="card__title">
              {t('pv.card.po')}
            </h3>
            <dl class="pv-fields">
              <Row label={t('pv.f.po')}>
                <a href={`#/po/${v.po.poNo}`} dir="ltr">
                  {v.po.poNo}
                </a>
              </Row>
              <Row label={t('pv.f.value')}>
                <span dir="ltr" class="mono">
                  {cur} {money(v.po.subtotal)} + {t('inv.vat')} {money(v.po.vat)} ={' '}
                  {money(v.po.total)}
                </span>
              </Row>
              <Row label={t('pv.f.terms')}>{t(`po.term.${v.po.paymentTerm}` as MessageKey)}</Row>
              <Row label={t('pv.f.cfo')}>
                {v.po.cfo.signed
                  ? t('pv.cfoSigned', { date: formatDate(v.po.cfo.at, lang) })
                  : t('pv.cfoPending')}
              </Row>
            </dl>
          </section>
          <section class="card" aria-labelledby="pv-sup">
            <h3 id="pv-sup" class="card__title">
              {t('pv.card.supplier')}
            </h3>
            <dl class="pv-fields">
              <Row label={t('pv.f.legal')}>
                <span dir="auto">{v.supplier.name}</span>
              </Row>
              <Row label={t('pv.f.crVat')}>
                <span dir="ltr" class="mono">
                  {v.supplier.cr || '—'} · {v.supplier.vat || '—'}
                </span>
              </Row>
              <Row label={t('pv.f.bank')}>{v.supplier.bankName || '—'}</Row>
              <Row label={t('pv.f.iban')}>
                <span dir="ltr" class="mono">
                  {v.supplier.iban || '—'}
                </span>
                <span class={`caption cell-sub ${v.supplier.holderMatches ? 'pv-ok' : 'pv-bad'}`}>
                  {v.supplier.holderMatches ? t('pv.holderOk') : t('pv.holderBad')}
                </span>
              </Row>
            </dl>
            <p class="caption">
              {t('pv.bankNote')}
              {v.supplier.bankChangedAt &&
                ` ${t('pv.bankChanged', { date: formatDate(v.supplier.bankChangedAt, lang) })}`}
            </p>
          </section>
          <section class="card" aria-labelledby="pv-dn">
            <h3 id="pv-dn" class="card__title">
              {t('pv.card.delivery')}
            </h3>
            <dl class="pv-fields">
              <Row label={t('pv.f.dn')}>
                {v.deliveries.length === 0
                  ? v.deliveryState === 'notRequired'
                    ? t('pv.service')
                    : '—'
                  : v.deliveries.map((d) => (
                      <span key={d.dnNo} class="cell-sub">
                        <a href={`#/dn/${d.dnNo}`} dir="ltr">
                          {d.dnNo}
                        </a>{' '}
                        · {d.partial ? t('dl.partial') : t('dl.full')} · {formatDate(d.date, lang)}
                      </span>
                    ))}
              </Row>
              <Row label={t('pv.f.invoice')}>
                <span dir="ltr">{v.invoice.invoiceNo}</span> · {formatDate(v.invoice.date, lang)}
              </Row>
              <Row label={t('pv.f.invoiceTotal')}>
                <span dir="ltr" class="mono">
                  {cur} {money(v.invoice.total)}
                </span>{' '}
                <span class={`caption ${v.invoice.match.matched ? 'pv-ok' : 'pv-bad'}`}>
                  {v.invoice.match.matched ? t('inv.matched') : t('inv.mismatch')}
                </span>
              </Row>
            </dl>
          </section>
          <section class="card card--info pv-pay" aria-labelledby="pv-amount">
            <div>
              <h3 id="pv-amount" class="pv-pay__label">
                {t('pv.payNow')}
              </h3>
              <p class="caption">
                {t(`pv.milestone.${v.payment.milestone}` as MessageKey)} · {v.payment.pct}% ·{' '}
                {t('pv.paidBefore')}{' '}
                <span dir="ltr">
                  {cur} {money(v.payment.paidBefore)}
                </span>{' '}
                · {t('pv.balanceAfter')}{' '}
                <span dir="ltr">
                  {cur} {money(v.payment.balanceAfter)}
                </span>
              </p>
            </div>
            <p class="pv-pay__amount" dir="ltr">
              {cur} {money(v.payment.due)}
            </p>
          </section>
        </div>

        <aside class="pv-side">
          <section class="card" aria-labelledby="pv-check">
            <h3 id="pv-check" class="card__title">
              {t('pv.checklist')}
            </h3>
            <ul class="inv-checks">
              {v.checklist.map((c) => (
                <li key={c.id} class={`inv-check inv-check--${c.ok ? 'ok' : 'fail'}`}>
                  <span aria-hidden="true">{c.ok ? '✓' : '✕'}</span>
                  <span>
                    {t(`pv.item.${c.id}` as MessageKey)}
                    {c.note && (
                      <span class="caption cell-sub">{t(`pv.note.${c.note}` as MessageKey)}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <p class="caption">{t('pv.checkHint')}</p>
          </section>
          <section class="card stack" aria-label={t('pv.actions')}>
            <button
              type="button"
              class="button"
              disabled={busy}
              aria-describedby="pv-pack-hint"
              onClick={() => void exportPack()}
            >
              {t('pack.export')}
            </button>
            <p class="caption" id="pv-pack-hint">
              {t('pack.hint')}
            </p>
            <a class="button" href={`#/vouchers/${v.pvNo}/print`}>
              {t('pv.print')}
            </a>
            {(v.status === 'Draft' || v.status === 'Returned') && (
              <>
                <button
                  type="button"
                  class="button button--primary"
                  disabled={busy || !v.canSend}
                  aria-describedby="pv-send-hint"
                  onClick={() => void send()}
                >
                  {t('pv.send')}
                </button>
                <p class="caption" id="pv-send-hint">
                  {v.canSend ? t('pv.sendHint') : t('pv.sendLocked')}
                </p>
              </>
            )}
          </section>
          {v.financeActions.length > 0 && (
            <FinancePanel
              v={v}
              onDone={(text) => {
                setNote({ ok: true, text });
                setReload((r) => r + 1);
              }}
            />
          )}
          <section class="card" aria-labelledby="pv-fin">
            <h3 id="pv-fin" class="card__title">
              {t('pv.finance')}
            </h3>
            <ul class="pv-timeline">
              <li class={v.sentAt ? 'pv-done' : ''}>
                {t('pv.t.sent')}
                {v.sentAt && <span class="caption cell-sub">{v.sentAt}</span>}
              </li>
              <li class={v.receivedAt ? 'pv-done' : ''}>
                {t('pv.t.received')}
                {v.receivedAt && (
                  <span class="caption cell-sub">
                    {v.receivedByName || v.receivedBy} · {v.receivedAt}
                  </span>
                )}
              </li>
              <li class={v.paidOn ? 'pv-done' : ''}>
                {t('pv.t.paid')}
                {v.paidOn && (
                  <span class="caption cell-sub">
                    {formatDate(v.paidOn, lang)} · <span dir="ltr">{v.paymentRef}</span>
                  </span>
                )}
              </li>
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

let adopted = false;
/** The document CSS as a constructed stylesheet: CSSOM is not inline style, so the CSP holds. */
function adoptStyles(): void {
  if (adopted || typeof CSSStyleSheet === 'undefined' || !('adoptedStyleSheets' in document)) {
    return;
  }
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(PV_CSS);
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  adopted = true;
}

export function VoucherPrintPage({ pvNo }: { pvNo: string }) {
  const { t } = useI18n();
  useEffect(adoptStyles, []);
  const load = useLoad<VoucherBody>(`/api/vouchers/${encodeURIComponent(pvNo)}`, 0);
  if (load.kind !== 'ok') return <Status load={load} />;
  return (
    <div class="stack">
      <div class="pod-toolbar no-print">
        <p class="caption">
          <a href={`#/vouchers/${pvNo}`} dir="ltr">
            {pvNo}
          </a>
        </p>
        <div class="actions">
          <button type="button" class="button" onClick={() => window.print()}>
            {t('pod.print')}
          </button>
        </div>
      </div>
      {/* Our own renderer's output: every value in it is escaped (shared/vouchers/document.ts). */}
      <div
        class="pod-sheet"
        dir="ltr"
        dangerouslySetInnerHTML={{ __html: renderPvDocument(load.data) }}
      />
    </div>
  );
}

/** The Finance inbox (P4.6): vouchers to receive, to pay, and returned to Procurement. */
export function FinanceInboxPage() {
  const { t, lang } = useI18n();
  const load = useLoad<InboxBody>('/api/finance/inbox', 0);
  if (load.kind !== 'ok') return <Status load={load} />;
  const data = load.data;
  const list = (id: string, title: MessageKey, items: InboxItem[]) => (
    <section class="card" aria-labelledby={id}>
      <h2 id={id} class="card__title">
        {t(title, { n: String(items.length) })}
      </h2>
      {items.length === 0 ? (
        <p class="caption">{t('fin.none')}</p>
      ) : (
        <ul class="action-list">
          {items.map((i) => (
            <li key={i.pvNo}>
              <a class="cfo-row" href={`#/vouchers/${i.pvNo}`}>
                <span class="req-row__title" dir="auto">
                  {i.title || i.prNo}
                </span>
                <span class="caption cell-sub">
                  <span dir="ltr">{i.pvNo}</span> · <span dir="ltr">{i.poNo}</span> · {i.vertical}
                  {i.sentAt && ` · ${formatDate(i.sentAt, lang)}`}
                </span>
                <span class="cfo-row__amount" dir="ltr">
                  {i.currency} {money(i.amount)}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
  return (
    <div class="stack">
      {!data.canAct && <p class="notice notice--info">{t('fin.readOnly')}</p>}
      {list('fin-in', 'fin.toReceive', data.toReceive)}
      {list('fin-pay', 'fin.toPay', data.toPay)}
      {list('fin-ret', 'fin.returned', data.returned)}
    </div>
  );
}
